import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { BarChart3, ChevronDown, ChevronUp, Layers3, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { rollupIdsFromParams } from '@/components/WavePlanning/routes'
import { toRollupGroups } from '@/components/WavePlanning/adapters'
import {
  WavePlanLoading,
  WavePlanLoadError,
} from '@/components/WavePlanning/WavePlanPageShell'
import { useWavePlanRollup } from '@/hooks/useWavePlans'
import { WavePlansBackLink } from '@/components/WavePlanning/WavePlansBackLink'
import {
  WaveRollupTable,
  type RollupTableTarget,
} from '@/components/WavePlanning/WaveRollupTable'
import {
  WaveRollupChart,
  type RollupChartActivation,
} from '@/components/WavePlanning/WaveRollupChart'
import {
  formatRollupValue,
  type RollupGroup,
  type RollupHighlight,
} from '@/components/WavePlanning/rollup-model'
import { useWaveRollupSelectionStore } from '@/stores/useWaveRollupSelectionStore'
import { getErrorMessage } from '@/utils/getErrorMessage'
import '@/components/WavePlanning/wave-rollup.css'

function CalendarRollup({
  group,
  active,
}: {
  group: RollupGroup
  active: boolean
}) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [highlight, setHighlight] = useState<RollupHighlight | null>(null)
  const [target, setTarget] = useState<RollupTableTarget | null>(null)
  const [allWeeks, setAllWeeks] = useState(false)
  const [chartsShown, setChartsShown] = useState(false)
  const weeks = allWeeks ? group.weeks : group.activeWeeks
  const toggle = (id: string) =>
    setExpanded(previous => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const selectHighlight = (
    next: RollupHighlight | null,
    activation?: RollupChartActivation
  ) => {
    setHighlight(next)
    if (next) setExpanded(previous => new Set([...previous, next.planId]))
    if (next && activation) {
      setTarget(previous => ({
        ...next,
        ...activation,
        requestId: (previous?.requestId ?? 0) + 1,
      }))
    }
  }

  return (
    <section
      className="rollup-calendar"
      aria-label={group.calendar.label + ' rollup'}
      data-testid={'rollup-calendar-' + group.calendar.id}
    >
      <dl className="rollup-summary">
        <div>
          <dt>Allocated units</dt>
          <dd data-testid="rollup-total-units">
            {formatRollupValue(group.season.units, 'units')}
          </dd>
        </div>
        <div>
          <dt>Season cube (ft³)</dt>
          <dd data-testid="rollup-total-cube">
            {formatRollupValue(group.season.cube, 'cube')}
          </dd>
        </div>
        <div>
          <dt>Allocated</dt>
          <dd
            className={
              group.season.percent !== null &&
              Math.abs(group.season.percent - 1) > 1e-7
                ? 'text-destructive'
                : ''
            }
            data-testid="rollup-total-percent"
          >
            {formatRollupValue(group.season.percent, 'percent')}
          </dd>
        </div>
        <div className="ml-auto self-center text-xs text-muted-foreground">
          {group.activeWeeks.length} active waves
        </div>
      </dl>
      <div className="rollup-workspace rounded-lg border bg-card">
        <div className="rollup-toolbar">
          <h2 className="mr-auto text-sm font-semibold">Plan rollup</h2>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={chartsShown}
            aria-controls={'charts-' + group.calendar.id}
            onClick={() => setChartsShown(previous => !previous)}
          >
            <BarChart3 size={15} />{' '}
            {chartsShown ? 'Hide charts' : 'Show charts'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded.size === group.plans.length}
            onClick={() =>
              setExpanded(
                expanded.size === group.plans.length
                  ? new Set()
                  : new Set(group.plans.map(row => row.plan.id))
              )
            }
          >
            {expanded.size === group.plans.length ? (
              <ChevronUp size={15} />
            ) : (
              <ChevronDown size={15} />
            )}
            {expanded.size === group.plans.length
              ? 'Collapse all'
              : 'Expand all'}
          </Button>
          <div className="flex items-center gap-2 text-xs">
            <Checkbox
              id={`rollup-all-weeks-${group.calendar.id}`}
              checked={allWeeks}
              onCheckedChange={setAllWeeks}
              className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
            />
            <label
              className="cursor-pointer"
              htmlFor={`rollup-all-weeks-${group.calendar.id}`}
            >
              Show all weeks
            </label>
          </div>
        </div>
        {highlight && (
          <div className="rollup-selection-bar">
            <span className="truncate">
              Highlighted:{' '}
              {
                group.plans.find(row => row.plan.id === highlight.planId)?.plan
                  .name
              }
              {highlight.week !== undefined &&
                ' · ' +
                  group.weeks.find(week => week.week === highlight.week)?.event}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0"
              onClick={() => selectHighlight(null)}
            >
              <X size={13} /> Clear highlight
            </Button>
          </div>
        )}
        <div
          id={'charts-' + group.calendar.id}
          hidden={!chartsShown}
          className="rollup-chart-panel"
        >
          <WaveRollupChart
            group={group}
            weeks={weeks}
            highlight={highlight}
            onHighlight={selectHighlight}
          />
        </div>
        <WaveRollupTable
          group={group}
          weeks={weeks}
          expanded={expanded}
          onToggle={toggle}
          highlight={highlight}
          target={target}
          active={active}
        />
      </div>
    </section>
  )
}

export default function WavePlanningRollup() {
  const [params, setParams] = useSearchParams()
  const ids = useMemo(() => rollupIdsFromParams(params), [params])
  const rollup = useWavePlanRollup(ids)
  const setSelectedPlanIds = useWaveRollupSelectionStore(
    state => state.setSelectedPlanIds
  )
  // The URL defines the rollup, including after reload or browser back/forward.
  useEffect(() => setSelectedPlanIds(ids), [ids, setSelectedPlanIds])
  const groups = useMemo(
    () => (rollup.data ? toRollupGroups(rollup.data.groups) : []),
    [rollup.data]
  )
  // The plans included, or the ones requested while the rollup is unavailable.
  const planCount = rollup.data
    ? groups.reduce((sum, group) => sum + group.plans.length, 0)
    : ids.length
  const activeCalendar =
    groups.find(group => group.calendar.id === params.get('calendarId'))
      ?.calendar.id ?? groups[0]?.calendar.id
  const unknownIds = rollup.data?.missing_ids ?? []
  const noneAvailable = !groups.length && unknownIds.length > 0

  return (
    <div className="wave-rollup">
      <header className="rollup-page-header">
        <div>
          <WavePlansBackLink />
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold">Rollup</h1>
            <p className="text-sm text-muted-foreground">
              {planCount} {planCount === 1 ? 'plan' : 'plans'} selected
            </p>
          </div>
        </div>
      </header>
      {unknownIds.length > 0 && !noneAvailable && (
        <p
          role="status"
          className="rounded-md border bg-muted/40 px-4 py-2 text-sm"
        >
          {unknownIds.length} unavailable{' '}
          {unknownIds.length === 1 ? 'plan was' : 'plans were'} omitted from
          this rollup.
        </p>
      )}
      {rollup.isError ? (
        <WavePlanLoadError
          className="m-0"
          error={rollup.error}
          onRetry={() => void rollup.refetch()}
          unavailable={{
            title: 'This rollup cannot be shown',
            message: getErrorMessage(
              rollup.error,
              'Check the selected plans and try again.'
            ),
          }}
          failedTitle="The rollup could not be loaded"
        />
      ) : rollup.isLoading ? (
        <WavePlanLoading label="Calculating rollup…" />
      ) : groups.length ? (
        <Tabs
          value={activeCalendar}
          className="rollup-tabs"
          onValueChange={calendarId => {
            setParams(previous => {
              const next = new URLSearchParams(previous)
              next.set('calendarId', calendarId)
              return next
            })
          }}
        >
          {groups.length > 1 ? (
            <TabsList
              variant="line"
              className="rollup-calendar-tabs"
              aria-label="Rollup calendar"
            >
              {groups.map(group => (
                <TabsTrigger key={group.calendar.id} value={group.calendar.id}>
                  {group.calendar.year}
                  <span className="ml-1 text-xs text-muted-foreground">
                    · {group.plans.length}{' '}
                    {group.plans.length === 1 ? 'plan' : 'plans'}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          ) : (
            <h2 className="text-sm font-semibold">
              {groups[0].calendar.label}
            </h2>
          )}
          {groups.map(group => (
            <TabsContent
              key={group.calendar.id}
              value={group.calendar.id}
              aria-label={group.calendar.label}
              forceMount
              hidden={group.calendar.id !== activeCalendar}
              className="rollup-calendar-content"
            >
              <CalendarRollup
                group={group}
                active={group.calendar.id === activeCalendar}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card className="flex flex-col items-center gap-3 p-12 text-center shadow-none">
          <Layers3 className="text-muted-foreground" size={28} />
          <h2 className="text-lg font-semibold">
            {noneAvailable
              ? 'Selected plans not available'
              : 'No plans selected'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {noneAvailable
              ? 'They may have been removed, or you may not have access to them.'
              : 'Select one or more plans to compare their waves.'}
          </p>
          <Button asChild>
            <Link to="/wave-planning">Select plans</Link>
          </Button>
        </Card>
      )}
    </div>
  )
}
